import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ArrowLeft, Stethoscope, Users, Check, Calendar } from '../components/Icons.jsx';

const placements = [
  {
    id: 'career-camp-london',
    title: 'Medicine Summer Career Camp — London',
    operator: 'Medic Mind',
    format: 'In-person',
    region: 'London',
    duration: '5 days',
    age: '15–18',
    tags: ['hospital', 'hospice', 'surgery', 'a-and-e', 'gp', 'mental-health'],
    why: 'Rotations across multiple NHS settings + hospice in a single week, led by surgeons and researchers. Bursary scheme covers 50–100% for widening-participation eligibility.',
    featured: true
  },
  {
    id: 'career-camp-manchester',
    title: 'Medicine Summer Career Camp — Manchester',
    operator: 'Medic Mind',
    format: 'In-person',
    region: 'Manchester',
    duration: '5 days',
    age: '15–18',
    tags: ['hospital', 'gp', 'surgery'],
    why: 'GP surgery placement + hospital rotations. Same Career Camp programme, Manchester cohort.',
    featured: true
  },
  {
    id: 'career-camp-birmingham',
    title: 'Medicine Summer Career Camp — Birmingham',
    operator: 'Medic Mind',
    format: 'In-person',
    region: 'Birmingham',
    duration: '5 days',
    age: '15–18',
    tags: ['hospital', 'workshops', 'research'],
    why: 'Workshops with active clinicians + hospital visits. Strong route for Year 12s outside the south-east.',
    featured: true
  },
  {
    id: 'virtual-we',
    title: 'Virtual Work Experience (free)',
    operator: 'Medic Mind',
    format: 'Virtual',
    region: 'Anywhere',
    duration: 'Self-paced',
    age: '14+',
    tags: ['virtual', 'reflection', 'gp', 'specialties'],
    why: 'Free. 8+ recorded sessions from active NHS clinicians, case-based discussions, reflection workbook. Good as a top-up — not a substitute for in-person.',
    featured: false
  },
  {
    id: 'career-camp-longitudinal',
    title: 'Career Camp + longitudinal add-on',
    operator: 'Medic Mind',
    format: 'In-person ongoing',
    region: 'Anywhere',
    duration: 'Career Camp week + 6+ months follow-on',
    age: '15–18',
    tags: ['hospice', 'longitudinal', 'communication', 'patient-contact'],
    why: 'Pair the Career Camp week with a Medic Mind-mentored longitudinal commitment in your home area. We help you scope a 6-month plan in the free 15-min call.',
    featured: false
  }
];

const regions = ['Any', 'London', 'Manchester', 'Birmingham', 'Anywhere (virtual / local)'];
const formats = ['Any', 'In-person', 'Virtual', 'Ongoing volunteering'];
const goals = ['Hospital rotations', 'Hospice / palliative', 'GP / community', 'Longitudinal volunteering', 'Virtual / reflection only'];

const goalToTags = {
  'Hospital rotations': ['hospital', 'surgery', 'a-and-e'],
  'Hospice / palliative': ['hospice', 'elderly', 'palliative'],
  'GP / community': ['gp', 'community'],
  'Longitudinal volunteering': ['longitudinal'],
  'Virtual / reflection only': ['virtual', 'reflection']
};

const STORAGE_KEY = 'we-log-v1';

export default function WeFinder() {
  const [region, setRegion] = useState('Any');
  const [format, setFormat] = useState('Any');
  const [goal, setGoal] = useState(goals[0]);

  const ranked = useMemo(() => {
    return placements
      .map((p) => {
        let score = p.featured ? 5 : 0;
        if (region !== 'Any' && (p.region === region || (region === 'Anywhere (virtual / local)' && p.region === 'Anywhere'))) score += 4;
        const fMap = { 'In-person': 'In-person', 'Virtual': 'Virtual', 'Ongoing volunteering': 'In-person ongoing' };
        if (format !== 'Any' && p.format === fMap[format]) score += 3;
        const goalTags = goalToTags[goal] || [];
        score += goalTags.filter((t) => p.tags.includes(t)).length * 2;
        return { ...p, score };
      })
      .sort((a, b) => b.score - a.score);
  }, [region, format, goal]);

  const [log, setLog] = useState(() => {
    try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]'); } catch { return []; }
  });
  const [draft, setDraft] = useState({ title: '', date: '', hours: '', setting: '' });

  const addToLog = () => {
    if (!draft.title.trim()) return;
    const next = [...log, { ...draft, id: Date.now() }];
    setLog(next);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    setDraft({ title: '', date: '', hours: '', setting: '' });
  };
  const removeEntry = (id) => {
    const next = log.filter((e) => e.id !== id);
    setLog(next);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  };
  const totalHours = log.reduce((sum, e) => sum + (Number(e.hours) || 0), 0);

  return (
    <>
      <div className="page-head">
        <Link to="/work-experience" className="back-link"><ArrowLeft width="16" height="16" />Back to Work experience</Link>
        <h1>Find work experience</h1>
        <p className="subtitle">Filter by region, format and goal. Track hours in a browser-local log.</p>
      </div>

      <div className="card" style={{ marginBottom: 18 }}>
        <h3 style={{ margin: '0 0 14px', fontSize: 18 }}>Filter</h3>
        <div className="r-grid-3">
          <Picker label="Region" value={region} onChange={setRegion} options={regions} />
          <Picker label="Format" value={format} onChange={setFormat} options={formats} />
          <Picker label="Goal" value={goal} onChange={setGoal} options={goals} />
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 14, marginBottom: 24 }}>
        {ranked.map((p) => (
          <div key={p.id} className="card" style={{ borderLeft: p.featured ? '5px solid var(--brand-purple)' : '5px solid transparent' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 14, flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: 240 }}>
                <div style={{ fontSize: 12, color: p.featured ? 'var(--accent)' : 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 700 }}>
                  {p.operator}{p.featured && ' · Recommended'}
                </div>
                <h3 style={{ margin: '4px 0 6px', fontSize: 18 }}>{p.title}</h3>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', fontSize: 13, color: 'var(--text-muted)' }}>
                  <Chip>{p.format}</Chip><Chip>{p.region}</Chip><Chip>{p.duration}</Chip><Chip>Ages {p.age}</Chip>
                </div>
                <p style={{ margin: '10px 0 0', color: 'var(--text-2)', fontSize: 14 }}>{p.why}</p>
              </div>
              {p.operator === 'Medic Mind' && (
                <Link to="/career-camp" className={p.featured ? 'btn btn-primary' : 'btn btn-secondary'}>Details <ArrowRight width="14" height="14" /></Link>
              )}
            </div>
          </div>
        ))}
      </div>

      <div className="card" style={{ marginBottom: 18 }}>
        <h2 style={{ margin: '0 0 4px', fontSize: 22 }}>Your work experience log</h2>
        <p style={{ margin: '0 0 14px', color: 'var(--text-muted)', fontSize: 14 }}>Tracked in your browser — nothing leaves this device. Export to your PS notes later.</p>

        <div className="r-stat-row" style={{ marginBottom: 14 }}>
          <Field label="Placement" value={draft.title} onChange={(v) => setDraft({ ...draft, title: v })} placeholder="e.g. Royal London A&E shadow" />
          <Field label="Date" value={draft.date} onChange={(v) => setDraft({ ...draft, date: v })} placeholder="Jun 2026" />
          <Field label="Hours" value={draft.hours} onChange={(v) => setDraft({ ...draft, hours: v })} placeholder="8" />
          <Field label="Setting" value={draft.setting} onChange={(v) => setDraft({ ...draft, setting: v })} placeholder="Hospital / GP / Hospice" />
          <button onClick={addToLog} className="btn btn-primary" style={{ height: 42 }}>Add</button>
        </div>

        {log.length === 0 ? (
          <div style={{ padding: 18, textAlign: 'center', color: 'var(--text-muted)', fontSize: 14, background: 'var(--card-soft)', borderRadius: 10 }}>No entries yet.</div>
        ) : (
          <>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {log.map((e) => (
                <div key={e.id} className="r-stat-row" style={{ padding: 10, background: 'var(--card-soft)', borderRadius: 10, fontSize: 14 }}>
                  <strong>{e.title}</strong>
                  <span>{e.date || '—'}</span>
                  <span>{e.hours || '—'}h</span>
                  <span>{e.setting || '—'}</span>
                  <button onClick={() => removeEntry(e.id)} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer' }}>Remove</button>
                </div>
              ))}
            </div>
            <div style={{ marginTop: 12, fontSize: 14, color: 'var(--text-2)' }}><strong>Total:</strong> {log.length} entries · {totalHours}h</div>
          </>
        )}
      </div>

      <section className="parents-cta" style={{ marginTop: 12 }}>
        <div>
          <h2>Bursaries: 50%, 75% or full.</h2>
          <p>If you're widening-participation eligible, Medic Mind covers up to 100% of Career Camp through the bursary scheme. Ask in your first call.</p>
        </div>
        <a className="btn">Apply for bursary <ArrowRight width="16" height="16" /></a>
      </section>
    </>
  );
}

function Picker({ label, value, onChange, options }) {
  return (
    <div>
      <label style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-2)' }}>{label}</label>
      <select value={value} onChange={(e) => onChange(e.target.value)} style={{ width: '100%', padding: '10px 12px', fontSize: 14, fontWeight: 600, border: '1px solid var(--border-strong)', borderRadius: 10, marginTop: 6, background: '#fff' }}>
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );
}

function Field({ label, value, onChange, placeholder }) {
  return (
    <div>
      <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-2)', display: 'block', marginBottom: 4 }}>{label}</label>
      <input value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} style={{ width: '100%', padding: '10px 12px', fontSize: 14, border: '1px solid var(--border-strong)', borderRadius: 10 }} />
    </div>
  );
}

function Chip({ children }) {
  return <span style={{ padding: '3px 9px', background: 'var(--card-soft)', borderRadius: 99, fontSize: 12 }}>{children}</span>;
}
